import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { 
  Calendar, 
  MapPin, 
  Flag, 
  Plane, 
  MessageSquare,
  TrendingUp,
  Users,
  Trophy,
  Loader2,
  ArrowRight,
  Clock,
  XCircle,
  CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DashboardPage = () => {
  const { golfer, user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState(null);
  const [teeTimes, setTeeTimes] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [feedRes, statsRes, teeTimesRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/feed`),
        axios.get(`${API_URL}/stats/platform`),
        axios.get(`${API_URL}/tee-times/my`, {
          headers: { Authorization: `Bearer ${await user?.getIdToken?.() || ''}` }
        }).catch(() => ({ data: { upcoming: [], past: [] } }))
      ]);
      setFeed(feedRes.data.feed);
      setStats(statsRes.data);
      setTeeTimes(teeTimesRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTeeTime = async (bookingId) => {
    try {
      const token = await user?.getIdToken?.();
      await axios.put(`${API_URL}/tee-times/${bookingId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeeTimes(prev => ({
        ...prev,
        upcoming: prev.upcoming.map(b => 
          b.booking_id === bookingId ? { ...b, status: 'cancelled' } : b
        )
      }));
    } catch {
      console.error('Failed to cancel tee time');
    }
  };

  const quickActions = [
    { label: 'Start Round', href: '/rounds/new', icon: Flag, color: 'bg-gold' },
    { label: 'Find Events', href: '/events', icon: Calendar, color: 'bg-fairway' },
    { label: 'Travel Mode', href: '/travel', icon: Plane, color: 'bg-deep-green' },
    { label: 'Messages', href: '/messages', icon: MessageSquare, color: 'bg-fairway' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-fairway to-deep-green rounded-sm p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-2xl md:text-3xl text-white mb-2">
              Welcome back, {golfer?.display_name || user?.display_name || 'Golfer'}!
            </h1>
            <p className="text-cream/80 font-body">
              {golfer?.home_city && golfer?.home_state 
                ? `Based in ${golfer.home_city}, ${golfer.home_state}`
                : 'Ready to connect with the golf community?'
              }
            </p>
          </div>
          <Link to="/rounds/new">
            <Button className="btn-gold" data-testid="start-round-btn">
              <Flag className="w-4 h-4 mr-2" />
              Start a Round
            </Button>
          </Link>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickActions.map((action) => (
          <Link key={action.label} to={action.href}>
            <Card className={`${action.color} hover:opacity-90 transition-opacity cursor-pointer`}>
              <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                <action.icon className="w-8 h-8 text-white mb-2" />
                <span className="text-white font-semibold text-sm">{action.label}</span>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* My Tee Times */}
      {teeTimes.upcoming.length > 0 && (
        <Card className="card-premium border-gold/20">
          <CardHeader>
            <CardTitle className="text-deep-green font-display flex items-center gap-2">
              <Clock className="w-5 h-5 text-gold" />
              Upcoming Tee Times
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {teeTimes.upcoming.map((booking) => (
                <div 
                  key={booking.booking_id} 
                  className={`flex items-center justify-between p-4 rounded-sm border ${
                    booking.status === 'cancelled' 
                      ? 'bg-red-50/50 border-red-200' 
                      : 'bg-green-50/50 border-green-200'
                  }`}
                  data-testid={`tee-time-${booking.booking_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-sm flex flex-col items-center justify-center ${
                      booking.status === 'cancelled' ? 'bg-red-100' : 'bg-fairway'
                    }`}>
                      <span className={`text-xs font-bold uppercase ${
                        booking.status === 'cancelled' ? 'text-red-600' : 'text-gold'
                      }`}>
                        {new Date(booking.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                      <span className={`text-lg font-bold ${
                        booking.status === 'cancelled' ? 'text-red-700' : 'text-white'
                      }`}>
                        {new Date(booking.date + 'T00:00:00').getDate()}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-deep-green">{booking.course_name}</p>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {booking.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" /> {booking.players} player{booking.players > 1 ? 's' : ''}
                        </span>
                        {booking.course_city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {booking.course_city}, {booking.course_state}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {booking.status === 'cancelled' ? (
                      <Badge variant="outline" className="text-red-600 border-red-300">Cancelled</Badge>
                    ) : (
                      <>
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Confirmed
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelTeeTime(booking.booking_id)}
                          className="text-muted-foreground hover:text-red-600 text-xs"
                          data-testid={`cancel-tee-time-${booking.booking_id}`}
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats & Golfer Profile */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Golfer Stats */}
        <Card className="card-premium">
          <CardHeader>
            <CardTitle className="text-deep-green font-display flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-gold" />
              Your Stats
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Rounds Logged</span>
              <span className="font-bold text-deep-green">{golfer?.stats?.rounds_logged || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Cities Played</span>
              <span className="font-bold text-deep-green">{golfer?.stats?.cities_played || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Events Joined</span>
              <span className="font-bold text-deep-green">{golfer?.stats?.events_joined || 0}</span>
            </div>
            {golfer?.handicap && (
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-muted-foreground">Handicap</span>
                <span className="font-bold text-gold text-lg">{golfer.handicap}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Platform Stats */}
        <Card className="card-premium">
          <CardHeader>
            <CardTitle className="text-deep-green font-display flex items-center gap-2">
              <Users className="w-5 h-5 text-gold" />
              Community
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Golfers</span>
              <span className="font-bold text-deep-green">{stats?.golfers || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Courses</span>
              <span className="font-bold text-deep-green">{stats?.courses || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Events</span>
              <span className="font-bold text-deep-green">{stats?.events || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Leagues</span>
              <span className="font-bold text-deep-green">{stats?.leagues || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Quick Profile */}
        <Card className="card-premium">
          <CardHeader>
            <CardTitle className="text-deep-green font-display flex items-center gap-2">
              <Trophy className="w-5 h-5 text-gold" />
              Your Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                <span className="text-gold font-bold text-lg">
                  {golfer?.display_name?.charAt(0) || 'G'}
                </span>
              </div>
              <div>
                <p className="font-semibold text-deep-green">{golfer?.display_name || 'Member'}</p>
                <p className="text-sm text-muted-foreground capitalize">{golfer?.skill_level || 'Golfer'}</p>
              </div>
            </div>
            {golfer?.play_style_tags?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {golfer.play_style_tags.slice(0, 3).map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs capitalize">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            <Link to="/profile">
              <Button variant="outline" className="w-full" data-testid="view-profile-btn">
                View Profile
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Activity Feed */}
      <Card className="card-premium">
        <CardHeader>
          <CardTitle className="text-deep-green font-display">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {feed.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No recent activity</p>
              <p className="text-sm text-muted-foreground mt-1">
                Start exploring events and connecting with golfers!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {feed.map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-muted/30 rounded-sm">
                  {item.type === 'event' && (
                    <>
                      <Calendar className="w-5 h-5 text-gold mt-0.5" />
                      <div>
                        <p className="font-semibold text-deep-green">{item.data.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.data.city}, {item.data.state} • {new Date(item.data.start_at).toLocaleDateString()}
                        </p>
                      </div>
                    </>
                  )}
                  {item.type === 'travel_post' && (
                    <>
                      <Plane className="w-5 h-5 text-gold mt-0.5" />
                      <div>
                        <p className="font-semibold text-deep-green">
                          {item.data.golfer?.display_name || 'A golfer'} is traveling
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Visiting {item.data.destination_city}, {item.data.destination_state}
                        </p>
                      </div>
                    </>
                  )}
                  {item.type === 'round' && (
                    <>
                      <Flag className="w-5 h-5 text-gold mt-0.5" />
                      <div>
                        <p className="font-semibold text-deep-green">
                          Live Round at {item.data.course?.name || 'a course'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {item.data.player_uids?.length || 0} players
                        </p>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
